// Handing in homework, and reading the verdict on it — the part of the
// lesson's homework under the brief (pages/lesson/LessonHomework.tsx draws
// the brief).
//
// Files go up the moment they are picked, each with its own progress, so a
// slow attachment never costs somebody their written text and they can see
// which one is still on its way. The answer itself is sent once they are all
// there. The form disappears while a piece of work is waiting to be marked,
// because a second attempt would only be refused by the server anyway.
//
// What is typed, and the files already up, are kept in this browser
// (localStorage, per learner and per homework) until they are sent: a learner
// signed out here because they signed in on another device, a tab closed by
// mistake, a lesson left half-way, all come back to the answer as they left it.

import { useEffect, useMemo, useRef, useState } from 'react'
import type { FormEvent } from 'react'

import { ApiError, post } from '../api'
import type { ApiErrorBody, Homework as Brief, StoredFile, Submission, SubmissionFile } from '../api'
import { useAuth } from '../auth'
import { Dropzone, Icon, messageOf } from '../lib/ui'
import { useFormat, useT } from '../i18n'
import common from '../i18n/common'
import m from '../i18n/homework'

type Props = {
  lectureId: string
  homework: Brief
  onSubmitted: (s: Submission) => void
}

/** An attachment the learner has chosen, and how far it has got. */
type Attachment = {
  key: string
  name: string
  size: number
  /** 0..1 of the bytes sent so far. */
  progress: number
  /** Set once the file is on the server; until then it cannot be handed in. */
  stored?: StoredFile
  error?: string
}

/**
 * What the server takes from a learner (see files/learner.go): pictures,
 * documents and voice notes, never video or SVG. Listed by extension and by
 * type, because a phone's camera roll does not always name its files.
 */
const ACCEPT = [
  '.png,.jpg,.jpeg,.gif,.webp,.pdf,.txt,.mp3,.ogg,.oga,.m4a,.docx,.xlsx,.pptx',
  'image/png,image/jpeg,image/gif,image/webp,application/pdf,text/plain',
  'audio/mpeg,audio/ogg,audio/mp4,audio/x-m4a',
].join(',')

/** The server's limit on one learner upload. */
const MAX_BYTES = 25 * 1024 * 1024

export function Homework({ lectureId, homework, onSubmitted }: Props) {
  const t = useT(m)
  const tc = useT(common)
  const f = useFormat()
  const { user } = useAuth()

  // The draft this learner left on this homework, read once, and cut to what
  // the homework takes now: the limit may have been lowered since the files
  // went up, and a file that could never be sent must not ride along unseen.
  const draftKey = user ? keyOf(user.id, homework.id) : null
  const [left] = useState(() => fitDraft(readDraft(draftKey), homework.max_files))
  const [body, setBody] = useState(left?.body ?? '')
  const [files, setFiles] = useState<Attachment[]>(() =>
    (left?.files ?? []).map((stored, i) => ({
      key: `kept${i}`,
      name: stored.filename,
      size: stored.size_bytes,
      progress: 1,
      stored,
    })),
  )
  const [restored, setRestored] = useState(left !== null && (left.body.trim() !== '' || left.files.length > 0))
  const [droppedFromDraft, setDroppedFromDraft] = useState((left?.dropped ?? 0) > 0)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const uploads = useRef(new Map<string, AbortController>())
  const nextKey = useRef(0)

  // Uploads still running when the lesson is left are abandoned, not kept
  // burning the learner's connection for a form nobody will send.
  useEffect(() => {
    const running = uploads.current
    return () => running.forEach((c) => c.abort())
  }, [])

  // The draft follows the form a moment after each change, and at once when
  // the page goes away — closed, reloaded, hidden, or replaced by the sign-in
  // page because the session ended — so the last words typed are kept too.
  // A file the server turned away is not kept: it would only be turned away
  // again the next time the answer is opened.
  const draft = useMemo<Draft>(
    () => ({ body, files: files.flatMap((a) => (a.stored && !a.error ? [a.stored] : [])) }),
    [body, files],
  )
  const latest = useRef(draft)
  latest.current = draft
  useEffect(() => {
    if (!draftKey) return
    const timer = window.setTimeout(() => keepDraft(draftKey, latest.current), DRAFT_DELAY_MS)
    return () => window.clearTimeout(timer)
  }, [draftKey, draft])
  useEffect(() => {
    if (!draftKey) return
    const flush = () => keepDraft(draftKey, latest.current)
    const flushIfHidden = () => {
      if (document.visibilityState === 'hidden') flush()
    }
    window.addEventListener('pagehide', flush)
    document.addEventListener('visibilitychange', flushIfHidden)
    return () => {
      window.removeEventListener('pagehide', flush)
      document.removeEventListener('visibilitychange', flushIfHidden)
      flush()
    }
  }, [draftKey])

  const submissions = homework.submissions ?? []
  const latestSubmission = submissions[0]
  const waiting = latestSubmission?.status === 'pending'
  const room = homework.max_files - files.length

  function take(chosen: File[]) {
    setError(null)
    const fits = chosen.slice(0, Math.max(0, room))
    if (fits.length < chosen.length) setError(tooMany(homework.max_files))

    // Added to the list first, so the person sees what they picked at once
    // and watches each one arrive rather than waiting on a frozen button.
    const added: Attachment[] = fits.map((file) => ({
      key: `a${nextKey.current++}`,
      name: file.name,
      size: file.size,
      progress: 0,
      error: file.size > MAX_BYTES ? t('tooLarge', { name: file.name }) : undefined,
    }))
    setFiles((current) => [...current, ...added])

    fits.forEach((file, i) => {
      const a = added[i]
      if (!a || a.error) return
      const controller = new AbortController()
      uploads.current.set(a.key, controller)
      const update = (patch: Partial<Attachment>) =>
        setFiles((current) => current.map((x) => (x.key === a.key ? { ...x, ...patch } : x)))

      sendFile(file, (fraction) => update({ progress: fraction }), controller.signal)
        .then((stored) => update({ stored, progress: 1 }))
        .catch((err: unknown) => {
          if (controller.signal.aborted) return
          update({ error: messageOf(err, m) })
        })
        .finally(() => uploads.current.delete(a.key))
    })
  }

  function remove(key: string) {
    uploads.current.get(key)?.abort()
    uploads.current.delete(key)
    setFiles((current) => current.filter((a) => a.key !== key))
  }

  /** "At most n files", or, where the homework takes none, to remove them. */
  function tooMany(max: number): string {
    return max > 0 ? t('tooMany', { max }) : t('noFilesTaken')
  }

  /**
   * What a refused answer tells the learner. A file the server no longer has
   * for them is marked on its own line, where it can be removed, rather than
   * leaving every later send to fail the same way; a limit lowered while the
   * page was open asks for a reload, which shows the new one.
   */
  function refusalOf(err: unknown, sent: Attachment[]): string {
    if (err instanceof ApiError && err.status === 422) {
      const gone = new Set<string>()
      for (const field of Object.keys(err.fields)) {
        const at = /^files\[(\d+)\]$/.exec(field)
        const a = at ? sent[Number(at[1])] : undefined
        if (a) gone.add(a.key)
      }
      if (gone.size > 0) {
        setFiles((current) => current.map((a) => (gone.has(a.key) ? { ...a, error: t('fileGone') } : a)))
        return t('dropFailed')
      }
      if (err.fields['files'] !== undefined) return t('filesLimitChanged')
    }
    return messageOf(err, m)
  }

  async function send(e: FormEvent) {
    e.preventDefault()
    if (busy) return

    if (files.some((a) => !a.stored && !a.error)) {
      setError(t('waitForUploads'))
      return
    }
    if (files.some((a) => a.error)) {
      setError(t('dropFailed'))
      return
    }
    if (files.length > homework.max_files) {
      setError(tooMany(homework.max_files))
      return
    }
    const sent = files.filter((a) => a.stored)
    const ready = sent.flatMap((a) => (a.stored ? [a.stored.id] : []))
    if (body.trim() === '' && ready.length === 0) {
      setError(t('needSomething'))
      return
    }

    setBusy(true)
    setError(null)
    try {
      const created = await post<Submission>(`/learn/lectures/${lectureId}/submissions`, {
        body: body.trim(),
        files: ready,
      })
      // Sent: nothing is left to keep. Cleared before anything re-renders, so
      // a flush on the way out cannot write the sent answer back.
      latest.current = EMPTY_DRAFT
      if (draftKey) keepDraft(draftKey, EMPTY_DRAFT)
      setBody('')
      setFiles([])
      setRestored(false)
      setDroppedFromDraft(false)
      onSubmitted(created)
    } catch (err) {
      setError(refusalOf(err, sent))
    } finally {
      setBusy(false)
    }
  }

  const size = (bytes: number) =>
    bytes < 1024
      ? t('sizeBytes', { n: f.number(bytes) })
      : bytes < 1024 * 1024
        ? t('sizeKb', { n: f.number(Math.round(bytes / 1024)) })
        : t('sizeMb', { n: f.number(Math.round((bytes / 1024 / 1024) * 10) / 10) })

  return (
    <div className="homework__answer">
      {homework.acceptance_required ? (
        <p className="muted">{t('acceptanceNote')}</p>
      ) : (
        homework.required && <p className="muted">{t('requiredNote')}</p>
      )}

      {submissions.length > 0 && (
        <>
          <h3 className="visually-hidden">{t('historyTitle')}</h3>
          <ol className="homework__history">
            {submissions.map((s, i) => (
              <Verdict key={s.id} submission={s} attempt={submissions.length - i} size={size} />
            ))}
          </ol>
        </>
      )}

      {waiting ? (
        <p className="muted" role="status">
          {t('waiting')}
        </p>
      ) : (
        <form onSubmit={(e) => void send(e)} noValidate>
          {restored && (
            <p className="homework__draft-note" role="status">
              <Icon name="check" size={16} />
              {t('draftRestored')}
            </p>
          )}
          {droppedFromDraft && (
            <p className="homework__draft-note homework__draft-note--warn" role="status">
              <Icon name="alert" size={16} />
              {homework.max_files > 0
                ? t('draftFilesDropped', { max: homework.max_files })
                : t('draftFilesDroppedAll')}
            </p>
          )}
          <label className="field">
            <span>{latestSubmission ? t('resendLabel') : t('answerLabel')}</span>
            <textarea
              rows={5}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={t('answerPlaceholder')}
              aria-describedby={draftKey ? `homework-draft-${homework.id}` : undefined}
            />
          </label>
          {draftKey && (
            <p id={`homework-draft-${homework.id}`} className="homework__draft-hint">
              {t('draftHint')}
            </p>
          )}

          {/* The list is drawn whenever it holds something, even where the
              homework takes no files now: whatever is in it is sent, so
              it must be visible and removable. */}
          {(homework.max_files > 0 || files.length > 0) && (
            <div className="homework__files">
              {homework.max_files > 0 && (
                <Dropzone
                  accept={ACCEPT}
                  multiple
                  disabled={room <= 0 || busy}
                  onFiles={take}
                  title={t('attachTitle')}
                  hint={t('attachHint')}
                >
                  <div className="dropzone__hint">
                    {room > 0 ? t('filesLeft', { n: room }) : t('filesFull', { max: homework.max_files })}
                  </div>
                </Dropzone>
              )}

              {files.length > 0 && (
                <ul>
                  {files.map((a) => (
                    <li key={a.key} className={a.error ? 'is-bad' : undefined}>
                      <span title={a.name}>{a.name}</span>
                      {!a.stored && !a.error && (
                        <div
                          className="bar bar--slim"
                          style={{ width: 96, flex: 'none' }}
                          role="progressbar"
                          aria-label={t('uploadProgress', { name: a.name })}
                          aria-valuemin={0}
                          aria-valuemax={100}
                          aria-valuenow={Math.round(a.progress * 100)}
                        >
                          <div className="bar__fill" style={{ width: `${Math.round(a.progress * 100)}%` }} />
                        </div>
                      )}
                      <span className="muted">
                        {a.error ??
                          (a.stored
                            ? size(a.size)
                            : tc('uploadingPercent', { percent: Math.round(a.progress * 100) }))}
                      </span>
                      <button
                        type="button"
                        className="link"
                        aria-label={t('removeFile', { name: a.name })}
                        onClick={() => remove(a.key)}
                      >
                        {tc('remove')}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {error && (
            <p className="field__error" role="alert">
              {error}
            </p>
          )}

          <button type="submit" className="button" disabled={busy}>
            {busy ? t('submitting') : t('submit')}
          </button>
        </form>
      )}
    </div>
  )
}

// ------------------------------------------------------------------ draft --

/** An answer not sent yet: what is typed, and the files already on the server. */
type Draft = { body: string; files: StoredFile[] }

const EMPTY_DRAFT: Draft = { body: '', files: [] }

/** How long typing pauses before the draft is written. */
const DRAFT_DELAY_MS = 600

/** One learner's draft of one homework. Learners share browsers; ids keep them apart. */
function keyOf(userId: string, homeworkId: string): string {
  return `mymentor.homework-draft.${userId}.${homeworkId}`
}

/**
 * The draft kept under key, or null when there is none worth restoring.
 * Storage that is switched off, full, or holding something this code did not
 * write is no draft at all.
 */
function readDraft(key: string | null): Draft | null {
  if (!key) return null
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { body?: unknown; files?: unknown }
    const body = typeof parsed.body === 'string' ? parsed.body : ''
    const files = (Array.isArray(parsed.files) ? parsed.files : []).filter(isStoredFile)
    return body.trim() === '' && files.length === 0 ? null : { body, files }
  } catch {
    return null
  }
}

/**
 * A kept draft cut to the files the homework takes now — none when it takes
 * none — with how many were left out, so the learner can be told.
 */
function fitDraft(draft: Draft | null, maxFiles: number): (Draft & { dropped: number }) | null {
  if (!draft) return null
  const room = Math.max(0, Math.floor(maxFiles) || 0)
  const files = draft.files.slice(0, room)
  return { body: draft.body, files, dropped: draft.files.length - files.length }
}

/** Writes the draft under key, or removes it when there is nothing in it. */
function keepDraft(key: string, draft: Draft) {
  try {
    if (draft.body.trim() === '' && draft.files.length === 0) localStorage.removeItem(key)
    else localStorage.setItem(key, JSON.stringify({ body: draft.body, files: draft.files, saved_at: Date.now() }))
  } catch {
    // Storage switched off or full: the draft lasts as long as the page does.
  }
}

function isStoredFile(v: unknown): v is StoredFile {
  const f = v as Partial<StoredFile> | null
  return (
    typeof f === 'object' &&
    f !== null &&
    typeof f.id === 'string' &&
    typeof f.filename === 'string' &&
    typeof f.size_bytes === 'number' &&
    typeof f.mime === 'string'
  )
}

function Verdict({
  submission,
  attempt,
  size,
}: {
  submission: Submission
  attempt: number
  size: (bytes: number) => string
}) {
  const t = useT(m)
  const f = useFormat()
  const tone = submission.status === 'graded' ? 'ok' : submission.status === 'rejected' ? 'bad' : 'wait'
  const label =
    submission.status === 'graded'
      ? t('statusGraded')
      : submission.status === 'rejected'
        ? t('statusRejected')
        : t('statusPending')
  const files: SubmissionFile[] = Array.isArray(submission.files) ? submission.files : []

  return (
    <li className={`verdict verdict--${tone}`}>
      <div className="verdict__head">
        <strong>{label}</strong>
        {submission.status === 'graded' && submission.grade != null && (
          <span className="verdict__grade">{t('gradeValue', { grade: f.number(submission.grade) })}</span>
        )}
        <span className="muted">{t('attemptN', { n: attempt })}</span>
        <span className="muted">{t('sentAt', { date: f.dateTime(submission.created_at) })}</span>
      </div>
      {submission.body && <p className="verdict__body">{submission.body}</p>}
      {files.length > 0 && (
        <ul className="verdict__files">
          {files.map((file) => (
            <li key={file.file_id}>
              <a
                href={`/files/${file.file_id}`}
                download={file.filename}
                aria-label={t('downloadFile', { name: file.filename })}
              >
                <Icon name="download" size={14} /> {file.filename}
              </a>
              {file.size_bytes ? <span className="muted"> · {size(file.size_bytes)}</span> : null}
            </li>
          ))}
        </ul>
      )}
      {submission.review_comment && (
        <p className="verdict__comment">
          <strong>{t('reviewerComment')}:</strong> {submission.review_comment}
        </p>
      )}
      {submission.status === 'rejected' && <p className="verdict__comment muted">{t('rejectedHint')}</p>}
      {submission.reviewed_at && submission.status !== 'pending' && (
        <p className="verdict__comment muted">{t('reviewedAt', { date: f.dateTime(submission.reviewed_at) })}</p>
      )}
    </li>
  )
}

/**
 * Sends one file to the learner upload route, reporting how much of it has
 * left the browser. XMLHttpRequest rather than fetch, because fetch cannot say
 * how far a request body has got — and a bar that cannot move is no bar. The
 * refusal is read exactly as the rest of the app reads one.
 */
function sendFile(file: File, onProgress: (fraction: number) => void, signal: AbortSignal): Promise<StoredFile> {
  return new Promise<StoredFile>((resolve, reject) => {
    const form = new FormData()
    form.append('file', file)

    const xhr = new XMLHttpRequest()
    xhr.open('POST', '/api/learn/files')
    xhr.withCredentials = true
    xhr.upload.onprogress = (e) => {
      const total = e.lengthComputable ? e.total : file.size
      if (total > 0) onProgress(Math.min(1, e.loaded / total))
    }
    xhr.onload = () => {
      let parsed: unknown = null
      if (xhr.responseText) {
        try {
          parsed = JSON.parse(xhr.responseText)
        } catch {
          // A proxy refusing the size answers in HTML, not in our error shape.
          reject(new ApiError(xhr.status, { code: 'bad_response', message: xhr.responseText.slice(0, 200) }))
          return
        }
      }
      if (xhr.status < 200 || xhr.status >= 300) {
        const body: ApiErrorBody = (parsed as { error?: ApiErrorBody } | null)?.error ?? {
          code: 'unknown',
          message: '',
        }
        reject(new ApiError(xhr.status, body))
        return
      }
      resolve(parsed as StoredFile)
    }
    // Nothing arrived: messageOf says so in the reader's language.
    xhr.onerror = () => reject(new TypeError('upload did not reach the server'))
    xhr.onabort = () => reject(new DOMException('upload cancelled', 'AbortError'))
    signal.addEventListener('abort', () => xhr.abort(), { once: true })
    xhr.send(form)
  })
}
